import { concat, reduce } from 'lodash';
import axiosClient from '@lib/utils/axiosClient';
import React from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';

const basePath = 'faxDocumentCategories';

export type TFaxDocumentCategory = {
  assignee: string;
  autoApprove: { enabled: boolean; threshold: number };
  code: string;
  createdt: string;
  createdBy: string;
  dateParsingRule: { fieldName: string };
  deleted: number;
  description: string;
  enabled: boolean;
  fieldsToExtract: string[];
  fileNameFormat: string;
  keywords: string[];
  name: string;
  type: string;
  updatedt: string;
  updatedBy: string;
  user: string;
  __v: number;
  _id: string;
};

export type Page = {
  response: TFaxDocumentCategory[];
  size: number;
};

export function useFaxDocumentCategoryPagination(
  params: { limit?: number; assignee?: string; _sort?: string; name?: string },
  options?: UseInfiniteQueryOptions<Page, Error, Page, Page, string[]>,
) {
  const paramsWithDefaults = { limit: params.limit || 10, ...params };

  const variant = 'v2/{basePath}';
  const url = variant.replace('{basePath}', basePath);

  async function queryFn(page: number) {
    const response = await axiosClient.get(url, { params: { page, ...paramsWithDefaults } });
    return response.data;
  }

  const stringParams = JSON.stringify(paramsWithDefaults);
  const queryKey = React.useMemo(() => ['faxDocumentCategories', stringParams], [stringParams]);

  const { data, isLoading, ...query } = useInfiniteQuery(queryKey, ({ pageParam = 1 }) => queryFn(pageParam), {
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.response.length < paramsWithDefaults.limit) {
        return undefined;
      }
      return allPages.length + 1;
    },
    staleTime: 2,
    ...options,
  });

  const allItems = React.useMemo(
    () =>
      reduce(
        data?.pages,
        (result: TFaxDocumentCategory[], page) => concat(result, page.response),
        [] as TFaxDocumentCategory[],
      ),
    [data?.pages],
  );

  return { allItems, isLoading, ...query };
}
